// The table view of a chart: the same data as columns and rows for DataTable. Give the result to the
// `table` prop of ChartCard, so a reader can read every value without a pointer.
// The columns keep the order and the roles of the chart, so each key line has the colour of its line.
import { FormatOptions, ValueFormat } from './chartFormat';
import type { BarChartSeries } from './BarChart';
import type { HeatmapRow } from './Heatmap';
import type { DataTableColumn, DataTableRow } from './DataTable';
import type { LineChartBand, LineChartSeries } from './LineChart';

export type ChartTableData = { columns: DataTableColumn[], rows: DataTableRow[] };

type LineChartTableInput = {
	dates: string[],
	series: LineChartSeries[],
	band?: LineChartBand,
	format: ValueFormat,
} & FormatOptions;

// One row per date: the date, one column per line, and two columns (min, max) for the band.
export const lineChartTable = ({ dates, series, band, format, currency, decimals }: LineChartTableInput): ChartTableData => {
	const value = (id: string, label: string, role?: LineChartSeries['role']): DataTableColumn =>
		({ id, label, format, currency, decimals, role });
	const columns: DataTableColumn[] = [
		{ id: '__date', label: 'Date', format: 'date' },
		...series.map(s => value(s.id, s.label, s.role ?? 'series')),
		...(band === undefined ? [] : [value('__lo', `${band.label} min`), value('__hi', `${band.label} max`)]),
	];
	const rows: DataTableRow[] = dates.map((date, i) => {
		const cells: DataTableRow['cells'] = { __date: date };
		series.forEach(s => { cells[s.id] = s.values[i] ?? null; });
		if (band !== undefined) {
			cells.__lo = band.lo[i] ?? null;
			cells.__hi = band.hi[i] ?? null;
		}
		return { id: date, cells };
	});
	return { columns, rows };
};

type BarChartTableInput = {
	categories: string[],
	series: BarChartSeries[],
	// The label of the category column, for example "Channel".
	categoryLabel: string,
	format: ValueFormat,
} & FormatOptions;

// One row per category: the category and one column per bar series.
export const barChartTable = ({ categories, series, categoryLabel, format, currency, decimals }: BarChartTableInput): ChartTableData => ({
	columns: [
		{ id: '__category', label: categoryLabel },
		...series.map(s => ({ id: s.id, label: s.label, format, currency, decimals, role: s.role ?? 'series' })),
	],
	rows: categories.map((category, i) => {
		const cells: DataTableRow['cells'] = { __category: category };
		series.forEach(s => { cells[s.id] = s.values[i] ?? null; });
		return { id: category, cells };
	}),
});

type HeatmapTableInput = {
	columns: string[],
	rows: HeatmapRow[],
	// The label of the row column, for example "Hotel".
	rowLabel: string,
	// The label of the total column; without it, no total column.
	totalLabel?: string,
	format: ValueFormat,
} & FormatOptions;

// One row per heatmap row: the label, one column per heatmap column, and the total. A closed cell is
// empty ('–'); a row with a note has no values.
export const heatmapTable = ({ columns, rows, rowLabel, totalLabel, format, currency, decimals }: HeatmapTableInput): ChartTableData => ({
	columns: [
		{ id: '__row', label: rowLabel },
		...columns.map(c => ({ id: c, label: c, format, currency, decimals })),
		...(totalLabel === undefined ? [] : [{ id: '__total', label: totalLabel, format, currency, decimals }]),
	],
	rows: rows.map(row => {
		const cells: DataTableRow['cells'] = { __row: row.label };
		columns.forEach((c, i) => { cells[c] = row.values?.[i] ?? null; });
		if (totalLabel !== undefined) cells.__total = row.total ?? null;
		return { id: row.id, cells };
	}),
});
